import { Fragment, useMemo, useState } from 'react';
import { Message } from '@gonfalon/messages/Message';
import { msg } from '@gonfalon/messages/msg';
import type { schemas } from '@gonfalon/openapi/types';
import { JsonSnippet } from '@gonfalon/ui/Snippet';
import { Cell, Column, Row, Table, TableBody, TableHeader } from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';

import { getEventPreviewTableData } from './eventPreviewTableDataUtils';

import styles from './EventDataPreview.module.css';

export type EventDataPreviewTableProps = {
  metricEventInstances: Array<schemas['MetricEventDefinitionInstance']>;
  isNumeric?: boolean;
  eventKind: string;
};

function splitEventData(event: schemas['MetricEventDefinitionInstance']) {
  const { trackJsonData, ...eventData } = event;
  const hasEventProperties = trackJsonData !== null && trackJsonData !== undefined && typeof trackJsonData === 'object';
  const eventProperties = hasEventProperties ? (trackJsonData as Record<string, unknown>) : null;
  return { eventData, eventProperties };
}

export function EventDataPreviewTable({
  metricEventInstances,
  isNumeric = false,
  eventKind,
}: EventDataPreviewTableProps) {
  const eventPreviewColumns = getEventPreviewTableData(eventKind, isNumeric);
  const [expandedRowIndex, setExpandedRowIndex] = useState<number | null>(null);

  const toggleRow = (index: number) => {
    setExpandedRowIndex((prev) => (prev === index ? null : index));
  };

  const splitEvents = useMemo(() => metricEventInstances.map(splitEventData), [metricEventInstances]);

  return (
    <div className={styles.tableScrollContainer}>
      <Table aria-label={msg('metrics.eventDataPreview.tableLabel')} className={styles.eventPreviewTable}>
        <TableHeader>
          <Column key="expand" isRowHeader className={styles.expandColumn}>
            {/* Empty header for expand column */}
          </Column>
          {eventPreviewColumns.map((col) => (
            <Column key={col.key} isRowHeader className={styles[`${col.key}Header`]}>
              {col.label}
            </Column>
          ))}
          <Column key="empty" isRowHeader className={styles.emptyCell}></Column>
        </TableHeader>
        <TableBody>
          {metricEventInstances.map((event, index) => {
            const isExpanded = expandedRowIndex === index;
            const { eventData, eventProperties } = splitEvents[index];
            return (
              <Fragment key={`${event.eventTimeMs}-${index}`}>
                <Row className={styles.tableRow}>
                  <Cell onClick={() => toggleRow(index)} className={styles.expandCellContainer}>
                    <div className={styles.expandCellContent}>
                      <Icon
                        name="chevron-right"
                        size="small"
                        className={`${styles.expandIcon} ${isExpanded ? styles.expanded : ''}`}
                      />
                    </div>
                  </Cell>
                  {eventPreviewColumns.map((col) => (
                    <Cell key={col.key} className={styles[col.key]}>
                      {col.value(event) || '-'}
                    </Cell>
                  ))}
                  <Cell className={styles.emptyCell} />
                </Row>
                <Row className={`${styles.expandedRowContainer} ${isExpanded ? styles.expanded : ''}`}>
                  <Cell
                    colSpan={eventPreviewColumns.length + 2}
                    className={`${styles.expandedRow} ${isExpanded ? styles.expanded : ''}`}
                  >
                    <div className={styles.expandedRowContent}>
                      <div className={styles.expandedRowSections}>
                        <section className={styles.expandedRowSection}>
                          <h4 className={styles.expandedRowSectionHeader}>
                            <Message id="metrics.eventDataPreview.eventDataColumn" />
                          </h4>
                          <JsonSnippet object={eventData} withCopyButton className={styles.jsonSnippet} />
                        </section>
                        {eventProperties && Object.keys(eventProperties).length > 0 && (
                          <section className={styles.expandedRowSection}>
                            <h4 className={styles.expandedRowSectionHeader}>
                              <Message id="metrics.eventDataPreview.eventPropertiesColumn" />
                            </h4>
                            <JsonSnippet object={eventProperties} withCopyButton className={styles.jsonSnippet} />
                          </section>
                        )}
                      </div>
                    </div>
                  </Cell>
                </Row>
              </Fragment>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
